import { startTransition } from 'react';
import { ChangeScope,mutationScope,scopeAffectsRead,validChangeScope } from './dataScopes';
import { clearQueryCache, invalidateQueries,readSessionKey } from './queryCache';
import { affectedDomains, DATA_DOMAINS, DataDomain, domainsForCacheKey } from './dataDomains';
export type DataChange = {
    domains: readonly DataDomain[]; scope?:ChangeScope; url: string; method: string; data?: any;
    reason: 'mutation' | 'external' | 'focus' | 'manual';
};
type Listener = (change: DataChange) => void;
const listeners = new Set<Listener>();
let channel: BroadcastChannel | undefined;
const writes = new Map<string, Promise<unknown>>();
let newSessionWrite = 0;
const creationWriteGuards = new Set<(url:string)=>void|Promise<void>>();
export function registerCreationWriteGuard(guard:(url:string)=>void|Promise<void>) {
    creationWriteGuards.add(guard);
    return () => {creationWriteGuards.delete(guard);};
}
/** Upload authorization/verification does not mutate a business record. */
export function isMediaPreparation(url:string,options:RequestInit|undefined):boolean {
    if(options?.method?.toUpperCase()!=='POST')return false;
    const path=url.split('?')[0];
    if(/\/erp\/import-uploads(?:\/chunk)?$/.test(path))return true;
    if(/\/supplier\/media\/uploads$|\/product-entry-media\/(?:ticket|complete)$|\/procurements\/identify-pending-receipts$/.test(path))return true;
    if(/\/livestreams\/\d+\/work-media\/[^/]+$/.test(path)) {
        try{const data=JSON.parse(String(options.body));return !!data&&typeof data==='object'&&('upload' in data||'uploads' in data);}catch{return false;}
    }
    return false;
}
function isGroupingQuote(url:string,options:RequestInit|undefined):boolean {
    return options?.method?.toUpperCase()==='POST' &&
        /\/ai-planning\/grouping-experiments\/[^/]+\/runs\/[^/]+\/placements\/[^/]+\/quote$/.test(url.split('?')[0]);
}
/** Serialize writes to one record; unrelated records still run concurrently. */
export function sequenceWrite<T>(url: string, options: RequestInit | undefined, send: () => Promise<T>): Promise<T> {
    if (['GET', 'HEAD', 'OPTIONS'].includes((options?.method ?? 'GET').toUpperCase())||isMediaPreparation(url,options)||isGroupingQuote(url,options)) return send();
    const path = url.split('?')[0];
    if (options?.method?.toUpperCase()==='POST' && /\/erp\/(?:pricing\/calculate|products\/bulk-edit\/prepare|product-selection\/targets\/search)$/.test(path)) return send();
    // The first-write receipt is the prerequisite of this queued write.
    if (/\/retain-creation(?:-draft)?$/.test(path)) return send();
    const scope = readSessionKey();
    const parent = `${scope}|${path.match(/^(.*?\/\d+)(?:\/|$)/)?.[1] ?? path}`;
    let key = parent;
    // Independent metadata fields and session rows may progress together. Whole-record
    // workflows remain barriers, so completion/stock actions cannot overtake edits.
    if (/\/(livestreams|procurements)\/\d+$/.test(path) && /[?&]view=fields(?:&|$)/.test(url) && options?.method?.toUpperCase() === 'PATCH') {
        try { const fields = Object.keys(JSON.parse(String(options.body))); if (fields.length === 1 && fields[0] !== 'series' && (!path.includes('/procurements/') || ['name','description','number_date','expected_arrival_date','tracking_numbers'].includes(fields[0]))) key = `${parent}|field:${fields[0]}`; } catch { /* Server validates malformed bodies. */ }
    }
    if (/\/returned-orders\/\d+\/fields$/.test(path) && options?.method?.toUpperCase() === 'PATCH') {
        try {const fields=Object.keys(JSON.parse(String(options.body)));if(fields.length===1)key=`${parent}|field:${fields[0]}`;} catch { /* Server validates the payload. */ }
    }
    if (/\/(?:inventory|products)\/\d+\/fields$/.test(path) && options?.method?.toUpperCase() === 'PATCH') {
        try {const fields=Object.keys(JSON.parse(String(options.body)));if(fields.length===1&&['name','product_note','purchase_url'].includes(fields[0]))key=`${parent}|field:${fields[0]}`;} catch { /* Server validates the payload. */ }
    }
    const session = path.match(/\/livestreams\/\d+\/sessions(?:\/(\d+))?$/);
    if (session) key = `${parent}|session:${session[1] ?? `new:${++newSessionWrite}`}`;
    const work = path.match(/\/livestreams\/\d+\/(work|work-media)\/(.+)$/);
    if (work) key = `${parent}|work:${new URLSearchParams(url.split('?')[1]??'').get('session_id') ?? 'series'}:${work[1]}:${work[2]}`;
    if (work?.[1]==='work' && work[2]==='preparation/title.text') key = `${parent}|field:title`;
    if (/\/(?:products|suppliers|masters)\/\d+\/(?:media|avatar|replace-photo|assets)(?:\/|$)/.test(path)) key=`${parent}|media`;
    const media=key===`${parent}|media`;
    const sendCurrent = () => scope === readSessionKey() ? send() : Promise.reject(new DOMException('Session changed', 'AbortError'));
    const guarded = () => {
        if(scope!==readSessionKey())return Promise.reject(new DOMException('Session changed','AbortError'));
        try {
            const pending=[...creationWriteGuards].map(guard=>guard(url)).filter((task):task is Promise<void>=>!!task);
            // Ordinary saved records have no creation receipt to retain. Start their
            // request in this click, before React paints the busy list.
            return pending.length?Promise.all(pending).then(sendCurrent):sendCurrent();
        }catch(error){return Promise.reject(error);}
    };
    const dependencies = key === parent
        ? Array.from(writes).filter(([other]) => (other === parent || other.startsWith(`${parent}|`)) && other!==`${parent}|media`).map(([, value]) => value)
        : [media?undefined:writes.get(parent), writes.get(key)].filter((value): value is Promise<unknown> => !!value);
    const result = dependencies.length ? Promise.all(dependencies.map(value => value.catch(() => undefined))).then(guarded) : guarded();
    writes.set(key, result);
    void result.finally(() => { if (writes.get(key) === result) writes.delete(key); }).catch(() => {});
    return result;
}
function broadcast(domains: readonly DataDomain[], scope?:ChangeScope) {
    try { channel?.postMessage({ domains, scope }); } catch { /* A closed tab cannot undo a saved mutation. */ }
}
export function subscribeDataChanges(listener: Listener) {
    listeners.add(listener);
    return () => { listeners.delete(listener); };
}
function publish(change: DataChange) {
    const domains = new Set(change.domains);
    const workPath = change.reason === 'mutation' ? change.url.match(/\/livestreams\/\d+\/(?:work\/[^/?]+|work-media)(?=\/)/)?.[0] : undefined;
    const workReadPath = workPath?.replace(/\/work-media$/, '/work/preparation');
    const sessionCover = /\/livestreams\/\d+\/(?:work-media\/cover|work\/preparation\/cover\.media)(?:\?|$)/.test(change.url)
        && /[?&]session_id=\d+(?:&|$)/.test(change.url);
    const preparationTask = /\/livestreams\/\d+\/work\/preparation\/(?!cover\.done(?:\?|$))[^/?]+\.done(?:\?|$)/.test(change.url);
    const resourcePattern = /\/(streamers|procurement-suppliers|product-categories|procurement-warehouses|product-attributes|livestream-types|product-brands)(?:\/|\?|$)/;
    const changedSetting = change.reason === 'mutation' ? change.url.match(resourcePattern)?.[1] : undefined;
    if (change.reason !== 'focus') invalidateQueries(key => {
        if (!scopeAffectsRead(key,change.scope)) return false;
        if (workReadPath && !change.data?._sync?.live && !key.includes(workReadPath)) {
            const livePath=workReadPath.replace(/\/work\/[^/]+$/, '');
            const liveId=livePath.split('/').pop();
            const sessionSnapshot=key.includes(`${livePath}?`) && /[?&]view=(?:sessions|edit)(?:&|$)/.test(key)
                || key.replace(/^(?:view|page)\|/,'').startsWith(`live|sessions|${liveId}`);
            const homeCalendar=(sessionCover || preparationTask) && (key.startsWith('raw|') && /\/home\/calendar(?:\?|$)/.test(key)
                || key.replace(/^(?:view|page)\|/,'').startsWith('live|home-calendar|'));
            const posterList = sessionCover && (key.includes('/livestreams?') || key.replace(/^(?:view|page)\|/,'').startsWith('live|summary|'));
            if (!posterList && !homeCalendar && (!sessionSnapshot || !/[?&]session_id=\d+/.test(change.url))) return false;
        }
        // A label write must not cancel an unrelated in-flight warehouse lookup.
        const setting = key.startsWith('raw|') ? key.match(resourcePattern)?.[1] : undefined;
        if (changedSetting && setting && setting !== changedSetting) return false;
        return domainsForCacheKey(key).some(domain => domains.has(domain));
    }, false);
    // An observer must never turn an already committed write into a failed save.
    // Keep the saving control's receipt/feedback urgent; reconciled sibling lists
    // may render afterward. Cache invalidation above remains synchronous.
    const notify=()=>listeners.forEach(listener => { try { listener(change); } catch (error) { console.error('Data observer failed', error); } });
    if(change.reason==='mutation')startTransition(notify);else notify();
}
export function commitStreamChange(url: string, data: unknown) {
    const domains = affectedDomains(url);
    const scope=mutationScope(url,data,'POST');
    publish({ domains, scope, url, method: 'POST', data, reason: 'mutation' });
    broadcast(domains,scope);
}
export function refreshData(domains: readonly DataDomain[] = DATA_DOMAINS) {
    publish({ domains, url: '', method: 'GET', reason: 'manual' });
}
/** Called before a successful write is returned to its caller. GETs never publish. */
export async function syncMutationResponse(url: string, options: RequestInit | undefined, response: Response, session = readSessionKey()) {
    if (/\/erp\/label-tasks(?:[/?]|$)/.test(url)) return;
    if (isMediaPreparation(url,options)||isGroupingQuote(url,options)||/\/(?:product-print-labels|print-task-sources|print-task-products)\/batch(?:\?|$)/.test(url)) return;
    const method = (options?.method ?? 'GET').toUpperCase();
    if (!response.ok || ['GET', 'HEAD', 'OPTIONS'].includes(method)) return;
    if (/ndjson|event-stream/.test(response.headers?.get('content-type') ?? '')) return;
    if (/\/(preview|search|calculate|match|get-rating|export|validate|upload-ticket|retain-creation(?:-draft)?)(?:\?|$)/.test(url)) return;
    // These POSTs calculate/read data and never represent a saved business change.
    if (/\/api\/labeling\/customer\/(?:dropshipping\/quote|rate\/(?:zone-quotes|full-matrix))(?:\?|$)/.test(url)) return;
    const domains = affectedDomains(url);
    if (!domains.length) return;
    let data: unknown;
    if (typeof response.clone === 'function' && response.headers?.get('content-type')?.includes('application/json')) {
        try { data = await response.clone().json(); } catch { /* Empty/non-JSON success still invalidates. */ }
    }
    if(session !== readSessionKey() || data && typeof data==='object' && 'success' in data && data.success===false)return;
    const live = (data as any)?._sync?.live;
    if (live && typeof live.title==='string') {
        for(const domain of ['live','liveWork','purchase'] as const)if(!domains.includes(domain))domains.push(domain);
    }
    if((data as any)?.receipt_label_tasks?.length||(data as any)?.label_tasks?.length||(data as any)?.temporary_labels?.length||(data as any)?.order_products?.temporary_labels?.length){
        // Register committed label keys before notifying the form; business receipts already persist them on the server.
        try {
            const {rememberOrderLabelChange}=await import('../features/erp/orderLabelTasks');
            const entryKey=(data as any)?.order_products && options?.headers ? new Headers(options.headers).get('X-Product-Save-Key') : null;
            if(entryKey)await rememberOrderLabelChange(url,data,entryKey);
            else await rememberOrderLabelChange(url,data);
        }
        catch { /* The task store reports the failure; never turn a committed order into a failed save. */ }
    }
    const scope=mutationScope(url,data,method);
    publish({ domains, scope, url, method, data, reason: 'mutation' });
    // Broadcast invalidation only; never send payloads, credentials or user data.
    broadcast(domains,scope);
}
/** A single site-level lifecycle bridge; no per-page focus listeners. */
export function installDataSync() {
    let focusQueued = false;
    let lastFocus = 0;
    const focus = () => {
        if (document.visibilityState === 'hidden' || focusQueued || Date.now() - lastFocus < 1000) return;
        focusQueued = true;
        queueMicrotask(() => { focusQueued = false; lastFocus = Date.now(); publish({ domains: DATA_DOMAINS, url: '', method: 'GET', reason: 'focus' }); });
    };
    const storage = (event: StorageEvent) => {
        if (event.key === null || ['supplier_portal_session','authToken','isAuthenticated','userRole'].includes(event.key)) { clearQueryCache(); focus(); }
    };
    if (typeof BroadcastChannel !== 'undefined') {
        channel = new BroadcastChannel('erp-data-changes-v1');
        channel.onmessage = event => {
            const domains = event.data?.domains;
            if (Array.isArray(domains) && domains.every(domain => DATA_DOMAINS.includes(domain)))
                publish({ domains, scope:validChangeScope(event.data?.scope)?event.data.scope:undefined, url: '', method: 'GET', reason: 'external' });
        };
    }
    window.addEventListener('focus', focus);
    document.addEventListener('visibilitychange', focus);
    window.addEventListener('storage', storage);
    return () => {
        window.removeEventListener('focus', focus);
        document.removeEventListener('visibilitychange', focus);
        window.removeEventListener('storage', storage);
        channel?.close(); channel = undefined;
    };
}
